import * as Sentry from '@sentry/browser'
import React, { FormEvent, useState } from 'react'
import { flowRight as compose, isEmpty } from 'lodash'
import { RouteChildrenProps } from 'react-router-dom'
import { FormProvider, useForm } from 'react-hook-form'
import { yupResolver } from '@hookform/resolvers/yup'
import { useTranslation } from 'react-i18next'
import { useSetRecoilState } from 'recoil'
import { loaderState } from 'recoils/atoms'

import {
  BranchForm,
  withBranch,
  schemaBranchForm,
  initialBranchForm,
  BranchService,
  BREADCRUMBS_CREATE,
} from 'features/Branch'
import { Button, PromptGuard, Modal } from 'components'
import { Confirmation, ModalSuccess } from 'components/Modal'
import { withBackOfficeCms } from 'helpers/hocs'
import { routes } from 'helpers/routes'
import {
  useBreadcrumbs,
  useFormSubmitLoader,
  usePromptForm,
} from 'helpers/hooks'
import { formatForm } from 'features/Branch/format'

import type { BranchForForm, BranchForApiSubmit } from 'types'

const BranchCreate = ({ history }: RouteChildrenProps): JSX.Element => {
  const methods = useForm<BranchForForm>({
    defaultValues: initialBranchForm,
    resolver: yupResolver(schemaBranchForm),
  })
  const [shouldPrompt, setShouldPrompt] = usePromptForm<BranchForForm>(methods)
  const [isLoadingForm] = useFormSubmitLoader<BranchForForm>(methods)
  const [isLoading, setIsLoading] = useState<boolean>(false)
  const [isOpenConfirm, setIsOpenConfirm] = useState<boolean>(false)
  const [isOpenSuccess, setIsOpenSuccess] = useState<boolean>(false)
  const [payload, setPayload] = useState<BranchForApiSubmit>()
  const setLoader = useSetRecoilState(loaderState)
  const { t } = useTranslation(['common', 'layout'])

  useBreadcrumbs(BREADCRUMBS_CREATE)

  const callApi = async () => {
    if (payload) {
      try {
        setLoader(true)

        await BranchService.create(payload, handleOpenSuccess)
        setLoader(false)
      } catch (error) {
        Sentry.captureException(error)
        setLoader(false)
      }
    }
  }

  const beforeSubmit = (e: FormEvent<HTMLFormElement>) => {
    methods.setValue('currentState', 'onSubmit')
    methods.handleSubmit(setData)(e)
    methods.setValue('currentState', '')
  }

  const setAddressLoading = (loading: boolean) => {
    setIsLoading(loading)
  }

  const handleBack = (): void => {
    history.push(routes.branch.path)
  }

  const setData = (data: BranchForForm) => {
    const payload = formatForm(data)
    if (isEmpty(methods.errors) && payload) {
      setIsOpenConfirm(true)
      setPayload(payload)
    }
  }

  const renderConfirmModal = () => {
    return (
      isOpenConfirm && (
        <Modal
          isIcon={false}
          isOpen={isOpenConfirm}
          onClose={() => setIsOpenConfirm(false)}
        >
          <Confirmation
            title={t('common:confirmation.title.label')}
            description={t('common:confirmation.description.label')}
            confirmText={t('common:save.button')}
            cancelText={t('common:cancel.button')}
            onConfirm={callApi}
            onClose={() => setIsOpenConfirm(false)}
          />
        </Modal>
      )
    )
  }

  const renderSuccessModal = () => {
    return (
      isOpenSuccess && (
        <ModalSuccess
          isOpen={isOpenSuccess}
          description={t('common:success.label')}
          onClose={() => setIsOpenSuccess(false)}
        />
      )
    )
  }

  const handleOpenSuccess = async () => {
    setIsOpenSuccess(true)

    await setTimeout(() => {
      setShouldPrompt(false)
      handleBack()
    }, 2000)
  }

  return (
    <div className='container__form'>
      <PromptGuard when={shouldPrompt} />
      <FormProvider {...methods}>
        <form onSubmit={beforeSubmit}>
          <BranchForm setAddressLoading={setAddressLoading} />
          <div className='container__form__actions'>
            <Button variants='secondary' onClick={handleBack}>
              {t('common:cancel.button')}
            </Button>
            <Button type='submit' isLoading={isLoading || isLoadingForm}>
              {t('common:save.button')}
            </Button>
          </div>
        </form>
      </FormProvider>

      {renderConfirmModal()}
      {renderSuccessModal()}
    </div>
  )
}

export default compose(withBackOfficeCms, withBranch)(BranchCreate)
